import { Localized } from "@fluent/react"
import { Alert, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Stack, TextField } from "@mui/material"
import { If } from "../../../app/If"
import { createDefaultCompanySettings, DefaultCompanySetting, updateCompanySetting } from "./defaultCompanySettingsApi";
import { useState } from "react";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { useAppDispatch } from "../../../app/hooks";

interface CreateEditCompanySettingsDialogProps {
    mode: "create" | "edit";
    companySetting?: DefaultCompanySetting;
    onSaveSuccess: (companySetting: DefaultCompanySetting) => void;
    onCancel: () => void;
}

export const CreateEditCompanySettingsDialog: React.FC<CreateEditCompanySettingsDialogProps> = ({ mode, companySetting, onSaveSuccess, onCancel }) => {
    const dispatch = useAppDispatch();
    const [type, setType] = useState<string>(companySetting?.type ?? "");
    const [code, setCode] = useState<string>(companySetting?.code ?? "");
    const [value, setValue] = useState<string>(companySetting?.value ?? "");
    const [isActive, setIsActive] = useState<boolean>(companySetting?.isActive ?? false);
    const [isFixed, setIsFixed] = useState<boolean>(companySetting?.isFixed ?? false);

    const [serverError, setServerError] = useState("");
    const [saving, setSaving] = useState<boolean>(false);

    const handleTypeChange = (event) => setType(event.target.value);
    const handleCodeChange = (event) => setCode(event.target.value);
    const handleValueChange = (event) => setValue(event.target.value);
    const handleIsActiveChange = (event) => setIsActive(event.target.checked);
    const handleIsFixedChange = (event) => setIsFixed(event.target.checked);

    const handleConfirm = async () => {
        try {
            if(mode === "create") {
                const result = await dispatch(createDefaultCompanySettings({ type, code, value, isActive, isFixed }))
                if (createDefaultCompanySettings.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    onSaveSuccess(result.payload);
                } else {
                    let errorMsg = "error"
                    if (result.payload && result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title

                        }
                    }
                    setServerError(errorMsg)
                }
            } else if(mode === "edit" && companySetting) {
                const result = await dispatch(updateCompanySetting({ id: companySetting.id, type, code, value, isActive, isFixed }))
                if (updateCompanySetting.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    onSaveSuccess(result.payload);
                } else {
                    let errorMsg = "error"
                    if (result.payload && result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title

                        }
                    }
                    setServerError(errorMsg)
                }
            }
        }
        catch (error) {
            dispatch(showError("error"));
        }
        finally {
            setSaving(false)
        }
    }

    return (
        <>
            <DialogTitle>
                <If condition={mode === "create"} otherwise={
                    <Localized id="system-settings-edit">
                        <span>_Edytuj ustawienie systemowe</span>
                    </Localized>
                    }>
                    <Localized id="system-settings-new">
                        <span>_Dodaj nowe ustawienie systemowe</span>
                    </Localized>
                </If>
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={2} padding={2}>
                    <TextField 
                        fullWidth
                        value={type}
                        onChange={handleTypeChange}
                        label={<Localized id="type">_Typ</Localized>}
                    />
                    <TextField 
                        fullWidth
                        value={code}
                        onChange={handleCodeChange}
                        label={<Localized id="system-settings-code">_Kod</Localized>}
                    />
                    <TextField 
                        fullWidth
                        value={value}
                        onChange={handleValueChange}
                        label={<Localized id="system-settings-value">_Wartość</Localized>}
                    />
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isActive}
                                    onChange={handleIsActiveChange} />}
                            label={<Localized id="system-settings-active"><span>_Aktywne</span></Localized>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isFixed}
                                    onChange={handleIsFixedChange} />}
                            label={<Localized id="system-settings-fixed"><span>_Stała</span></Localized>}
                        />
                    </FormGroup>
                </Stack>
                {
                    serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleConfirm} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </>               
    )
}